import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Loader2, Link2, Unplug, CheckCircle2 } from 'lucide-react';
import { trpc } from '@/lib/trpc';
import { toast } from 'sonner';

/**
 * Xero Integration settings card.
 *
 * Self-contained: owns the Xero status query, connect/disconnect mutations,
 * and the OAuth-return handler (`?xero_connected` / `?xero_error`). It uses
 * independent tRPC mutations (not the Organization tab's profile save form),
 * so it can live on the Integrations tab.
 */
export function XeroIntegrationCard() {
  const navigate = useNavigate();
  const utils = trpc.useUtils();

  const xeroStatusQuery = trpc.xero.getStatus.useQuery(undefined, {
    staleTime: 30_000,
  });

  const xeroAuthMutation = trpc.xero.getAuthUrl.useMutation({
    onSuccess: (result) => {
      if (result.authUrl) {
        window.location.href = result.authUrl;
      }
    },
    onError: (error) => {
      toast.error(error.message || 'Failed to start Xero connection');
    },
  });

  const xeroDisconnectMutation = trpc.xero.disconnect.useMutation({
    onSuccess: async () => {
      await Promise.all([
        utils.xero.getStatus.invalidate(),
        utils.xero.getAccounts.invalidate(),
        utils.xero.getSyncLogs.invalidate(),
      ]);
      toast.success('Disconnected from Xero');
    },
    onError: (error) => {
      toast.error(error.message || 'Failed to disconnect from Xero');
    },
  });

  // Handle the Xero OAuth return (?xero_connected / ?xero_error) once on mount.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const connected = params.get('xero_connected');
    const error = params.get('xero_error');
    const tenant = params.get('tenant');

    if (!connected && !error) return;

    if (connected) {
      toast.success(tenant ? `Connected to Xero (${tenant})` : 'Connected to Xero');
      void utils.xero.getStatus.invalidate();
    }
    if (error) {
      const knownErrors: Record<string, string> = {
        access_denied: 'Access was denied during Xero authorization.',
        invalid_client: 'Xero client configuration error. Contact support.',
        server_error: 'Xero experienced a server error. Please try again.',
      };
      const message = knownErrors[error] ?? `Xero connection failed (${error.slice(0, 80)})`;
      toast.error(message);
    }

    params.delete('xero_connected');
    params.delete('xero_error');
    params.delete('tenant');
    const nextQuery = params.toString();
    const nextUrl = `${window.location.pathname}${nextQuery ? `?${nextQuery}` : ''}${window.location.hash}`;
    window.history.replaceState({}, '', nextUrl);
  }, [utils]);

  const handleConnectXero = () => {
    if (xeroAuthMutation.isPending) return;
    xeroAuthMutation.mutate({});
  };

  const handleDisconnectXero = () => {
    if (xeroDisconnectMutation.isPending) return;
    xeroDisconnectMutation.mutate();
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Xero Integration</CardTitle>
        <CardDescription>
          Connect Xero, sync accounts and tax rates, and control accounting dropdown visibility.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            {xeroStatusQuery.data?.connected ? (
              <Badge variant="success" className="gap-1">
                <CheckCircle2 className="h-3.5 w-3.5" />
                Connected{xeroStatusQuery.data.tenantName ? `: ${xeroStatusQuery.data.tenantName}` : ''}
              </Badge>
            ) : xeroStatusQuery.data?.configured ? (
              <Badge variant="warning">Not Connected</Badge>
            ) : (
              <Badge variant="destructive">Not Configured</Badge>
            )}
            {xeroStatusQuery.data?.expiresAt ? (
              <span className="text-xs text-muted-foreground">
                Token expires {new Date(xeroStatusQuery.data.expiresAt).toLocaleString()}
              </span>
            ) : null}
          </div>
          <div className="flex items-center gap-2">
            {!xeroStatusQuery.data?.connected ? (
              <Button
                type="button"
                onClick={handleConnectXero}
                disabled={
                  xeroAuthMutation.isPending ||
                  xeroStatusQuery.data?.configured === false
                }
              >
                {xeroAuthMutation.isPending ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Opening...
                  </>
                ) : (
                  <>
                    <Link2 className="mr-2 h-4 w-4" />
                    Connect Xero
                  </>
                )}
              </Button>
            ) : (
              <Button
                type="button"
                variant="outline"
                onClick={handleDisconnectXero}
                disabled={xeroDisconnectMutation.isPending}
              >
                {xeroDisconnectMutation.isPending ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Disconnecting...
                  </>
                ) : (
                  <>
                    <Unplug className="mr-2 h-4 w-4" />
                    Disconnect
                  </>
                )}
              </Button>
            )}
          </div>
        </div>

        {!xeroStatusQuery.data?.configured ? (
          <Alert>
            <AlertDescription>
              Xero OAuth is not configured on the backend yet. Add <code>XERO_CLIENT_ID</code>, <code>XERO_CLIENT_SECRET</code>, and <code>XERO_REDIRECT_URI</code>.
            </AlertDescription>
          </Alert>
        ) : null}

        {/* BLU-456: sync, COGS, stock-in-transit reclass and chart-of-accounts
            visibility moved to the dedicated /xero workspace. Keep only the
            connection here; point to the workspace for everything else. */}
        {xeroStatusQuery.data?.connected ? (
          <div className="flex items-start gap-3 rounded-md border bg-muted/40 px-3 py-3">
            <Link2 className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" aria-hidden="true" />
            <div className="space-y-1 text-sm">
              <p className="text-muted-foreground">
                Sync, COGS, stock-in-transit reclass and chart-of-accounts visibility now live on
                the dedicated Xero page.
              </p>
              <Button
                type="button"
                variant="link"
                className="h-auto p-0 text-threadheads-purple"
                onClick={() => navigate('/xero')}
              >
                Open the Xero workspace
              </Button>
            </div>
          </div>
        ) : null}
      </CardContent>
    </Card>
  );
}
